import React, { useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useImageContext } from '../contexts/ImageContext';
import { useAudio } from '../contexts/AudioContext';
import defaultSpinningDisk from '../images/defaultSpinningDisk.jpg';
import './css/MobileNavigation.css';

const MobileNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams();
  const { uploadedImage, imageName } = useImageContext();
  const { audioData, updatedRegion } = useAudio();
  const buttonRef = useRef(null);
  
  // Check if a real image has been uploaded (not the default)
  const hasUploadedImage = useMemo(() => {
    return uploadedImage && 
      (uploadedImage !== defaultSpinningDisk && 
       !uploadedImage.includes('defaultSpinningDisk') &&
       (uploadedImage.startsWith('data:') || imageName !== 'example.jpg'));
  }, [uploadedImage, imageName]);

  // Memoize steps array to prevent re-renders
  const steps = useMemo(() => [
    { 
      id: 1, 
      name: 'Image', 
      path: sessionId ? `/app/session/${sessionId}/images` : '/images',
      nextPath: sessionId ? `/app/session/${sessionId}/colours` : '/colours',
      nextText: 'Choose Colors'
    },
    { 
      id: 2, 
      name: 'Colors', 
      path: sessionId ? `/app/session/${sessionId}/colours` : '/colours',
      prevPath: sessionId ? `/app/session/${sessionId}/images` : '/images',
      prevText: 'Back to Image',
      nextPath: sessionId ? `/app/session/${sessionId}/audio` : '/audio',
      nextText: 'Add Audio'
    },
    { 
      id: 3, 
      name: 'Audio', 
      path: sessionId ? `/app/session/${sessionId}/audio` : '/audio',
      prevPath: sessionId ? `/app/session/${sessionId}/colours` : '/colours',
      prevText: 'Back to Colors',
      nextPath: sessionId ? `/app/session/${sessionId}/preview` : '/preview',
      nextText: 'Preview Design'
    },
    { 
      id: 4, 
      name: 'Preview', 
      path: sessionId ? `/app/session/${sessionId}/preview` : '/preview',
      prevPath: sessionId ? `/app/session/${sessionId}/audio` : '/audio',
      prevText: 'Back to Audio',
      nextPath: sessionId ? `/app/session/${sessionId}/export` : '/export',
      nextText: 'Export'
    },
    { 
      id: 5, 
      name: 'Export', 
      path: sessionId ? `/app/session/${sessionId}/export` : '/export',
      prevPath: sessionId ? `/app/session/${sessionId}/preview` : '/preview',
      prevText: 'Back to Preview'
    }
  ], [sessionId]);

  // Memoize current step to prevent unnecessary re-renders
  const currentStep = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/images')) return steps[0];
    if (path.includes('/colours')) return steps[1];
    if (path.includes('/audio')) return steps[2];
    if (path.includes('/preview')) return steps[3];
    if (path.includes('/export')) return steps[4];
    return null;
  }, [location.pathname, steps]);

  // Memoize disabled state to prevent re-renders (must be before early return)
  const isNextDisabled = useMemo(() => {
    if (!currentStep) return false;
    // Disable next button on Images page if no image uploaded
    if (currentStep.id === 1 && !hasUploadedImage) {
      return true;
    }
    // Disable next button on Audio page if no audio file
    if (currentStep.id === 3 && !audioData?.file) {
      return true;
    }
    // On export page, check if export is ready
    if (currentStep.id === 5) {
      // Export is ready if image, audio, and region are all set
      return !(hasUploadedImage && audioData?.file && updatedRegion);
    }
    return false;
  }, [currentStep, hasUploadedImage, audioData?.file, updatedRegion]);

  const handleBack = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep?.prevPath) {
      navigate(currentStep.prevPath);
    }
  }, [currentStep?.prevPath, navigate]);

  const handleNext = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // On export page, trigger export modal instead of navigating
    if (currentStep?.id === 5) {
      // Dispatch custom event for export page to handle
      window.dispatchEvent(new CustomEvent('exportVideo'));
      return;
    }
    if (currentStep?.nextPath && !isNextDisabled) {
      navigate(currentStep.nextPath);
    }
  }, [currentStep?.nextPath, currentStep?.id, isNextDisabled, navigate]);

  // Early return after all hooks
  if (!currentStep) {
    return null;
  }

  return (
    <div className="mobile-navigation">
      <div className="mobile-nav-buttons">
        {currentStep.prevPath && (
          <button 
            ref={buttonRef}
            className="mobile-nav-button mobile-nav-button-back"
            onClick={handleBack}
            onTouchStart={(e) => {
              // Prevent scroll and other touch behaviors
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBack(e);
            }}
            type="button"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>{currentStep.prevText}</span>
          </button>
        )}
        {(currentStep.nextPath || currentStep.id === 5) && (
          <button 
            className={`mobile-nav-button mobile-nav-button-next ${isNextDisabled ? 'disabled' : ''}`}
            onClick={handleNext}
            onTouchStart={(e) => {
              if (!isNextDisabled) {
                // Prevent scroll and other touch behaviors
                e.stopPropagation();
              }
            }}
            onTouchEnd={(e) => {
              if (!isNextDisabled) {
                e.preventDefault();
                e.stopPropagation();
                handleNext(e);
              }
            }}
            disabled={isNextDisabled}
            type="button"
          >
            <span>{currentStep.id === 5 ? 'Export Video' : currentStep.nextText}</span>
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileNavigation;

